import { ActionButton } from '../parts/index.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, Unauthenticated } from '../api.js';
import { LIVE_COALESCE_MS, useLiveRefresh } from '../live.js';

export function said(e: unknown): string {
  if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return 'signing you in again…'; }
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

/**
 * What went wrong, and the one thing to do about it. `onRetry` is given wherever the caller can
 * ask again: until 2026-09-21 a read that failed left a tab with an error and a "Reading the
 * connectors…" line under it and nothing to press, so the only way to try again was to reload
 * the page — and three of these tabs failed every time (module 10 AUDIT-2026-09-21-admin.md).
 */
export function Problem({ message, onRetry }: { message: string | null; onRetry?: () => void }) {
  if (!message) return null;
  return (
    <p className="admin-error" role="alert">
      {message}
      {onRetry ? <> <ActionButton reading type="button" className="admin-retry" onClick={onRetry}>Try again</ActionButton></> : null}
    </p>
  );
}

/**
 * One load and its error, re-run on demand. `amend` changes what was loaded without asking again; a
 * load still in the air when it is called was asked before the change and would put an older reading
 * over it, so that one is dropped and asked afresh.
 */
export function useLoad<T>(load: () => Promise<T>): { data: T | null; error: string | null; reload: () => void; amend: (change: (d: T) => T) => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const inTheAir = useRef(false);
  const startedAt = useRef(Number.NEGATIVE_INFINITY);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    inTheAir.current = true;
    startedAt.current = Date.now();
    load().then((d) => { if (!cancelled) setData(d); }).catch((e: unknown) => { if (!cancelled) setError(said(e)); })
      .finally(() => { if (!cancelled) inTheAir.current = false; });
    return () => { cancelled = true; };
  }, [load, n]);
  const reload = useCallback(() => setN((x) => x + 1), []);
  const amend = useCallback((change: (d: T) => T) => {
    setData((d) => (d === null ? d : change(d)));
    if (inTheAir.current) setN((x) => x + 1);
  }, []);

  /**
   * Read again when he comes back to the tab (B1265, live.ts). Every admin tab is one of these,
   * so this is the whole console in one place: connectors, brains, routes, the numbers and the
   * rest all showed whatever was true when the tab was opened.
   *
   * No beat, deliberately, and this is the surface the rule was written for: two of these reads
   * walk audit.judgement under a per-row policy and were measured in seconds on his store, so a
   * minute's clock here is not a page asking again, it is the store on its knees. The re-read
   * also leaves the table on the screen and says nothing when it fails -- a tab that is showing
   * good rows must not be replaced by an error because one return did not land.
   */
  const quiet = useCallback((signal: AbortSignal) => {
    if (inTheAir.current || Date.now() - startedAt.current < LIVE_COALESCE_MS) return;
    return load().then(
      (d) => { if (!signal.aborted) setData(d); },
      () => { /* what is on the screen stays */ },
    );
  }, [load]);
  useLiveRefresh(quiet);

  return { data, error, reload, amend };
}

